import { toast } from '@atrvd/saas-ui'

// The session-expired toast: the one error toast that outlives a navigation and
// a page reload (DCP-365 AC.4).
//
// Every other error toast is cleared when the user moves to another page — the
// message belongs to the screen that produced it. An expired session belongs to
// the SESSION, not to a screen: it survives whatever the user does next,
// because until they sign in again nothing else in the app will work, and the
// reason must not vanish while they are still looking for it.
//
// Three call sites raise it — AuthContext's SIGNED_OUT listener (primary),
// queryClient's 401 fallback, and the file-import poller — and they must agree
// on all of it: the same wording, the same id, the same survival rules. That
// agreement lives here rather than in three copies.
//
// Plain comments, not JSDoc: a /** */ block with no declaration under it gets
// attached to whatever follows by some tooling, which would put this overview
// in the hover for SESSION_EXPIRED_TOAST_ID (PR #210 review #6).

/**
 * Shared sonner id. Dedupes concurrent raises (several 401s in flight, or the
 * AuthContext listener and the queryClient fallback firing on the same event)
 * into ONE toast, and is what {@link isSessionExpiredToast} recognises so the
 * navigation sweep leaves it alone.
 */
export const SESSION_EXPIRED_TOAST_ID = 'session-expired'

/**
 * sessionStorage, not localStorage, and not a cookie.
 *
 * The flag must outlive a document reload — `queryClient` redirects with
 * `window.location.href`, which tears down every toast the moment after it is
 * raised — but it must NOT outlive the tab. In localStorage a flag written by
 * one expiry would greet the user in every future tab until something thought
 * to clear it; the failure mode of a stuck "your session has expired" on a
 * perfectly good session is worse than the bug being fixed.
 */
const STORAGE_KEY = 'dcp:session-expired-toast'

/**
 * Read/write helpers that never throw.
 *
 * Storage access itself raises in Safari private mode and wherever site data is
 * blocked — and this runs on the auth path, so a throw here would take down
 * sign-out and the 401 handler. Losing the flag degrades to today's behaviour
 * (the toast simply does not survive a reload), which is an acceptable
 * fallback; an exception is not.
 */
function rememberAcrossReload(): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, '1')
  } catch {
    // Storage unavailable — the toast still shows, it just won't come back
    // after a reload.
  }
}

function forgetAcrossReload(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do: if we cannot write we could not have stored it either.
  }
}

function wasRememberedAcrossReload(): boolean {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Raise the session-expired toast, and arrange for it to come back after a
 * reload.
 *
 * Idempotent by way of the shared id: calling it while the toast is already up
 * updates that toast instead of stacking a second one.
 */
export function showSessionExpiredToast(): void {
  rememberAcrossReload()
  toast.error('Your session has expired', {
    description: 'Please sign in to continue',
    duration: Infinity,
    id: SESSION_EXPIRED_TOAST_ID,
    // Fires when the user closes it — the cross or a swipe (DCP-365 AC.4.1).
    // Dismissing is the user saying they have read it, so it must not return on
    // the next reload. Programmatic dismissal clears the flag separately in
    // `clearSessionExpiredToast`, so the flag is cleared on both paths.
    onDismiss: forgetAcrossReload,
  })
}

/**
 * Re-raise the toast if it was showing when the document was torn down.
 *
 * Call once at startup, before the first render. Without this, AC.4's "stays on
 * UI even if user refreshes the page" cannot hold: toasts are in-memory, so a
 * reload takes them with it.
 */
export function restoreSessionExpiredToast(): void {
  if (wasRememberedAcrossReload()) {
    showSessionExpiredToast()
  }
}

/**
 * Take the toast down and stop it coming back.
 *
 * For the paths where the session stops being expired — an explicit sign-out,
 * or a fresh sign-in. Clearing the flag is the point: dismissing alone would
 * leave it stored, and the next reload would announce an expiry that has since
 * been resolved.
 */
export function clearSessionExpiredToast(): void {
  forgetAcrossReload()
  toast.dismiss(SESSION_EXPIRED_TOAST_ID)
}

/** Whether a toast id is the session-expired one (see the sweep in useDismissErrorToastsOnNavigate). */
export function isSessionExpiredToast(id: number | string): boolean {
  return id === SESSION_EXPIRED_TOAST_ID
}
